<template>
  <div class="dashboard-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader />
      <main class="page-content cs-page">

        <!-- Page header -->
        <div class="cs-header">
          <div class="cs-header-left">
            <div class="cs-header-icon">⭐</div>
            <div>
              <h1 class="cs-title">Customer Satisfaction</h1>
              <p class="cs-subtitle">Track CSAT ratings collected through your Enterprise Flows</p>
            </div>
          </div>
          <div class="cs-header-right">
            <button class="cs-export-btn" @click="exportCSV" title="Export filtered data as CSV">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10,19L6,15H9V11H11V15H14L10,19Z"/></svg>
              Export CSV
            </button>
          </div>
        </div>

        <!-- No-flow-mode banner -->
        <div v-if="!isCustomFlowMode" class="cs-no-flow-banner">
          <span class="cs-no-flow-icon">⚡</span>
          <div>
            <strong>Enterprise Flow Mode is not active</strong>
            <p>CSAT ratings are collected via <strong>CSAT Rating</strong> nodes in your Enterprise Flows. Switch to Enterprise Flow Mode from the dashboard to start collecting ratings.</p>
          </div>
        </div>

        <!-- Filters bar -->
        <div class="cs-filters">
          <!-- Period pills -->
          <div class="cs-period-pills">
            <button
              v-for="p in periods" :key="p.value"
              class="cs-period-pill"
              :class="{ active: period === p.value }"
              @click="setPeriod(p.value)"
            >{{ p.label }}</button>
          </div>

          <!-- Department filter -->
          <select v-model="filterDept" class="cs-filter-select" @change="loadAll">
            <option value="">All Departments</option>
            <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
          </select>

          <!-- Agent filter -->
          <select v-model="filterAgent" class="cs-filter-select" @change="loadAll">
            <option value="">All Agents</option>
            <option v-for="a in agents" :key="a.id" :value="a.id">{{ a.name }}</option>
          </select>

          <!-- Unsatisfied toggle -->
          <button
            class="cs-unsatisfied-btn"
            :class="{ active: showUnsatisfiedOnly }"
            @click="toggleUnsatisfied"
            title="Show only low-scoring customers"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,17L7,12L8.41,10.59L12,14.17L15.59,10.59L17,12L12,17M12,13L7,8L8.41,6.59L12,10.17L15.59,6.59L17,8L12,13Z"/></svg>
            Unsatisfied Only
          </button>
        </div>

        <!-- Summary cards -->
        <div class="cs-summary-row" v-if="!stats.loading">
          <!-- Average score -->
          <div class="cs-stat-card cs-stat-card--accent">
            <div class="cs-stat-label">Average Score</div>
            <div class="cs-stat-main">
              <span class="cs-stat-big">{{ stats.avg || '—' }}</span>
              <span class="cs-stat-denom" v-if="stats.avg">/ {{ stats.max_score || 5 }}</span>
            </div>
            <div class="cs-stars-row">
              <span
                v-for="i in (stats.max_score || 5)" :key="i"
                class="cs-star"
                :class="i <= Math.round(stats.avg) ? 'cs-star--filled' : 'cs-star--empty'"
              >★</span>
            </div>
          </div>

          <!-- Total ratings -->
          <div class="cs-stat-card">
            <div class="cs-stat-label">Total Ratings</div>
            <div class="cs-stat-main">
              <span class="cs-stat-big">{{ stats.total || 0 }}</span>
            </div>
            <div class="cs-stat-sub">responses collected</div>
          </div>

          <!-- Satisfaction rate -->
          <div class="cs-stat-card cs-stat-card--green">
            <div class="cs-stat-label">Satisfaction Rate</div>
            <div class="cs-stat-main">
              <span class="cs-stat-big">{{ satisfactionRate }}%</span>
            </div>
            <div class="cs-stat-sub">Scores ≥ {{ satisfiedThreshold }}/{{ stats.max_score || 5 }}</div>
          </div>

          <!-- Unsatisfied count -->
          <div class="cs-stat-card cs-stat-card--red">
            <div class="cs-stat-label">Unsatisfied</div>
            <div class="cs-stat-main">
              <span class="cs-stat-big">{{ unsatisfiedCount }}</span>
            </div>
            <div class="cs-stat-sub">Scores ≤ {{ unsatisfiedThreshold }}/{{ stats.max_score || 5 }}</div>
          </div>
        </div>
        <div v-else class="cs-summary-row">
          <div v-for="i in 4" :key="i" class="cs-stat-card cs-stat-loading"></div>
        </div>

        <!-- Distribution + Trend row -->
        <div class="cs-analytics-row">

          <!-- Score Distribution -->
          <div class="cs-analytics-card">
            <div class="cs-analytics-head">Score Distribution</div>
            <div v-if="stats.loading" class="cs-loading-rows">
              <div v-for="i in 5" :key="i" class="cs-loading-bar"></div>
            </div>
            <div v-else-if="!stats.distribution || !Object.keys(stats.distribution).length" class="cs-empty-state">
              <span>No data yet</span>
            </div>
            <div v-else class="cs-dist-rows">
              <div
                v-for="score in distributionScores" :key="score"
                class="cs-dist-row"
              >
                <span class="cs-dist-label">{{ score }} ★</span>
                <div class="cs-dist-bar-wrap">
                  <div
                    class="cs-dist-bar"
                    :class="distBarClass(score)"
                    :style="{ width: distPercent(score) + '%' }"
                  ></div>
                </div>
                <span class="cs-dist-count">{{ stats.distribution[score] || 0 }}</span>
                <span class="cs-dist-pct">{{ distPercent(score) }}%</span>
              </div>
            </div>
          </div>

          <!-- Trend over time -->
          <div class="cs-analytics-card">
            <div class="cs-analytics-head">Trend</div>
            <div v-if="stats.loading" class="cs-loading-rows">
              <div v-for="i in 5" :key="i" class="cs-loading-bar"></div>
            </div>
            <div v-else-if="!stats.trend || !stats.trend.length" class="cs-empty-state">
              <span>No data yet</span>
            </div>
            <div v-else class="cs-trend-wrap">
              <!-- Mini sparkline chart using SVG -->
              <svg class="cs-sparkline" viewBox="0 0 300 80" preserveAspectRatio="none">
                <polyline
                  :points="sparklinePoints"
                  fill="none"
                  stroke="#f59e0b"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <circle
                  v-for="(pt, idx) in sparklineCoords" :key="idx"
                  :cx="pt.x" :cy="pt.y" r="3"
                  fill="#f59e0b"
                />
              </svg>
              <!-- Period labels -->
              <div class="cs-trend-labels">
                <span v-for="t in stats.trend" :key="t.period" class="cs-trend-label">{{ t.period }}</span>
              </div>
              <!-- Average per period -->
              <div class="cs-trend-avgs">
                <div
                  v-for="t in stats.trend" :key="t.period + '-avg'"
                  class="cs-trend-avg-chip"
                  :title="`${t.count} ratings`"
                >
                  <span class="cs-trend-avg-score">{{ Number(t.avg_score).toFixed(1) }}</span>
                  <span class="cs-trend-avg-cnt">({{ t.count }})</span>
                </div>
              </div>
            </div>
          </div>

          <!-- By Department -->
          <div class="cs-analytics-card" v-if="stats.by_department && stats.by_department.length">
            <div class="cs-analytics-head">By Department</div>
            <div class="cs-by-rows">
              <div v-for="d in stats.by_department" :key="d.department_id" class="cs-by-row">
                <span class="cs-by-name">{{ d.department_name || '—' }}</span>
                <span class="cs-by-score">{{ Number(d.avg_score).toFixed(1) }} ★</span>
                <span class="cs-by-count">{{ d.count }}</span>
              </div>
            </div>
          </div>

        </div>

        <!-- Ratings Table -->
        <div class="cs-table-card">
          <div class="cs-table-head">
            <div class="cs-table-title">
              Individual Ratings
              <span class="cs-table-count" v-if="!entries.loading">({{ entries.total }})</span>
            </div>
            <div class="cs-table-search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/></svg>
              <input
                type="text"
                v-model="searchQuery"
                placeholder="Search by customer name or phone..."
                class="cs-search-input"
                @input="loadEntries(1)"
              />
            </div>
          </div>

          <!-- Table -->
          <div class="cs-table-wrap">
            <div v-if="entries.loading" class="cs-table-loading">
              <div class="loading-spinner"></div>
            </div>
            <table v-else class="cs-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Score</th>
                  <th>Department</th>
                  <th>Agent</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!entries.rows.length">
                  <td colspan="6" class="cs-table-empty">No ratings found for the selected filters.</td>
                </tr>
                <tr
                  v-for="row in entries.rows" :key="row.id"
                  :class="{ 'cs-row-unsatisfied': row.score <= unsatisfiedThreshold }"
                >
                  <td class="cs-td-customer">
                    <div class="cs-customer-name">{{ row.customer_name }}</div>
                    <div class="cs-customer-phone">{{ row.wa_id }}</div>
                  </td>
                  <td class="cs-td-score">
                    <div class="cs-score-badge" :class="scoreBadgeClass(row.score, row.max_score)">
                      {{ row.score }}/{{ row.max_score }}
                    </div>
                    <div class="cs-score-stars">
                      <span v-for="i in row.max_score" :key="i" class="cs-star-sm" :class="i <= row.score ? 'filled' : ''">★</span>
                    </div>
                  </td>
                  <td class="cs-td-dept">{{ row.department_name || '—' }}</td>
                  <td class="cs-td-agent">{{ row.agent_name || '—' }}</td>
                  <td class="cs-td-date">{{ formatDate(row.submitted_at) }}</td>
                  <td class="cs-td-actions">
                    <button
                      class="cs-action-btn cs-action-btn--profile"
                      @click="openProfile(row.wa_id)"
                      title="Open customer profile"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"/></svg>
                      Profile
                    </button>
                    <button
                      class="cs-action-btn cs-action-btn--chat"
                      @click="openChat(row.wa_id)"
                      title="Open WhatsApp chat"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2M12.05 3.67C14.25 3.67 16.31 4.53 17.87 6.09C19.42 7.65 20.28 9.72 20.28 11.92C20.28 16.46 16.58 20.15 12.04 20.15C10.56 20.15 9.11 19.76 7.85 19L7.55 18.83L4.43 19.65L5.26 16.61L5.06 16.29C4.24 15 3.8 13.47 3.8 11.91C3.81 7.37 7.5 3.67 12.05 3.67M8.53 7.33C8.37 7.33 8.1 7.39 7.87 7.64C7.65 7.89 7 8.5 7 9.71C7 10.93 7.89 12.1 8 12.27C8.14 12.44 9.76 14.94 12.25 15.97C14.74 17 14.74 16.68 15.18 16.68C15.62 16.68 16.59 16.11 16.79 15.56C17 15.01 17 14.54 16.93 14.44C16.86 14.34 16.7 14.27 16.45 14.15C16.21 14.03 15 13.43 14.77 13.35C14.55 13.27 14.39 13.23 14.23 13.48C14.07 13.72 13.59 14.27 13.45 14.44C13.31 14.61 13.17 14.63 12.92 14.51C12.67 14.39 11.87 14.13 10.91 13.27C10.16 12.6 9.65 11.78 9.51 11.53C9.37 11.28 9.49 11.15 9.61 11.03C9.72 10.92 9.86 10.74 9.98 10.6C10.1 10.46 10.14 10.36 10.22 10.2C10.3 10.03 10.26 9.89 10.2 9.77C10.14 9.65 9.65 8.43 9.44 7.95C9.24 7.49 9.03 7.55 8.87 7.54L8.53 7.33Z"/></svg>
                      Chat
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="entries.totalPages > 1" class="cs-pagination">
            <button
              class="cs-page-btn"
              :disabled="entries.page <= 1"
              @click="loadEntries(entries.page - 1)"
            >‹ Prev</button>
            <span class="cs-page-info">Page {{ entries.page }} of {{ entries.totalPages }}</span>
            <button
              class="cs-page-btn"
              :disabled="entries.page >= entries.totalPages"
              @click="loadEntries(entries.page + 1)"
            >Next ›</button>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { authStore } from '@/store/auth'
import { DashboardService } from '@/services/dashboard'

const API = 'https://api.quali-d.com'

export default {
  name: 'CustomerSatisfactionView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      authStore,
      period: '30d',
      filterDept: '',
      filterAgent: '',
      showUnsatisfiedOnly: false,
      searchQuery: '',

      periods: [
        { value: 'today', label: 'Today' },
        { value: '7d', label: '7 Days' },
        { value: '30d', label: '30 Days' },
        { value: '90d', label: '90 Days' },
        { value: 'all', label: 'All Time' }
      ],

      departments: [],
      agents: [],

      stats: {
        loading: true,
        avg: null,
        max_score: 5,
        total: 0,
        satisfied: 0,
        unsatisfied: 0,
        distribution: {},
        trend: [],
        by_department: [],
        by_agent: []
      },

      entries: {
        loading: true,
        rows: [],
        total: 0,
        page: 1,
        totalPages: 1
      }
    }
  },

  computed: {
    isCustomFlowMode () {
      return authStore.customFlowMode
    },

    satisfiedThreshold () {
      const max = this.stats.max_score || 5
      return max === 10 ? 7 : 4
    },

    unsatisfiedThreshold () {
      const max = this.stats.max_score || 5
      return max === 10 ? 4 : 2
    },

    satisfactionRate () {
      if (!this.stats.total) return 0
      return Math.round((this.stats.satisfied / this.stats.total) * 100)
    },

    unsatisfiedCount () {
      return this.stats.unsatisfied || 0
    },

    distributionScores () {
      const max = this.stats.max_score || 5
      const scores = []
      for (let i = max; i >= 1; i--) scores.push(i)
      return scores
    },

    sparklineCoords () {
      const trend = this.stats.trend || []
      if (trend.length < 2) return []
      const W = 300; const H = 80; const PAD = 10
      const scores = trend.map(t => parseFloat(t.avg_score) || 0)
      const minS = Math.max(0, Math.min(...scores) - 0.5)
      const maxS = Math.max(...scores) + 0.5
      return scores.map((s, i) => ({
        x: PAD + (i / (trend.length - 1)) * (W - PAD * 2),
        y: PAD + (1 - (s - minS) / (maxS - minS)) * (H - PAD * 2)
      }))
    },

    sparklinePoints () {
      return this.sparklineCoords.map(p => `${p.x},${p.y}`).join(' ')
    }
  },

  async mounted () {
    // Ensure the mode is fetched even if coming directly to this page or after a refresh
    if (!authStore.customFlowMode) {
      const result = await DashboardService.checkCurrentMode()
      authStore.setCustomFlowMode(result.mode === 'custom_flow', result.customFlowActiveId || null)
    }
    await this.loadFilters()
    await this.loadAll()
  },

  methods: {
    token () {
      return localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
    },

    headers () {
      return { Authorization: `Bearer ${this.token()}`, Accept: 'application/json' }
    },

    dateRange () {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      const fmt = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      const today = fmt(now)
      if (this.period === 'today') return { from: today, to: today }
      if (this.period === 'all') return { from: null, to: null }
      const days = { '7d': 7, '30d': 30, '90d': 90 }[this.period] || 30
      const from = new Date(now); from.setDate(from.getDate() - days + 1)
      return { from: fmt(from), to: today }
    },

    buildParams (extras = {}) {
      const { from, to } = this.dateRange()
      const p = new URLSearchParams()
      if (from) p.set('from_date', from)
      if (to) p.set('to_date', to)
      if (this.filterDept) p.set('department_id', this.filterDept)
      if (this.filterAgent) p.set('agent_id', this.filterAgent)
      Object.entries(extras).forEach(([k, v]) => v !== undefined && v !== '' && p.set(k, v))
      return p.toString()
    },

    setPeriod (val) {
      this.period = val
      this.loadAll()
    },

    toggleUnsatisfied () {
      this.showUnsatisfiedOnly = !this.showUnsatisfiedOnly
      this.loadEntries(1)
    },

    async loadFilters () {
      try {
        const [deptRes, agentRes] = await Promise.all([
          fetch(`${API}/departments`, { headers: this.headers() }),
          fetch(`${API}/agents`, { headers: this.headers() })
        ])
        const deptData = await deptRes.json()
        const agentData = await agentRes.json()
        this.departments = deptData.departments || deptData || []
        this.agents = agentData.agents || agentData || []
      } catch (e) {
        console.warn('[CSAT] loadFilters error', e)
      }
    },

    async loadAll () {
      await Promise.all([this.loadStats(), this.loadEntries(1)])
    },

    async loadStats () {
      this.stats.loading = true
      try {
        const res = await fetch(`${API}/customers/csat/stats?${this.buildParams()}`, { headers: this.headers() })
        const data = await res.json()
        if (data.success) {
          const s = data.summary || {}

          // distribution is an array [{score, max_score, count}] — convert to {score: count} map
          const distArr = Array.isArray(data.distribution) ? data.distribution : []
          const dist = {}
          distArr.forEach(d => { dist[d.score] = d.count })

          // Derive max_score from first distribution item (all rows share the same scale)
          const max = distArr.length ? parseInt(distArr[0].max_score) : 5

          // avg_score_pct is a percentage (e.g. "80.0" = 80% of max_score) → convert to actual score
          const avgPct = parseFloat(s.avg_score_pct) || 0
          const avgScore = avgPct ? (avgPct / 100) * max : null

          const total = parseInt(s.total_responses) || 0
          const satThr = max === 10 ? 7 : 4
          const unsatThr = max === 10 ? 4 : 2
          let satisfied = 0; let unsatisfied = 0
          Object.entries(dist).forEach(([score, count]) => {
            const n = parseInt(score)
            if (n >= satThr) satisfied += parseInt(count)
            if (n <= unsatThr) unsatisfied += parseInt(count)
          })

          // Normalise trend: API uses {date, responses, avg_score_pct}
          const trend = (data.trend || []).map(t => ({
            ...t,
            period: t.date,
            count: t.responses,
            avg_score: t.avg_score_pct ? ((parseFloat(t.avg_score_pct) / 100) * max).toFixed(1) : '0'
          }))

          // Normalise by_department: look up name from loaded departments list
          const byDept = (data.by_department || []).map(d => ({
            ...d,
            count: d.responses,
            avg_score: d.avg_score_pct ? ((parseFloat(d.avg_score_pct) / 100) * max).toFixed(1) : '0',
            department_name: (this.departments.find(dep => String(dep.id) === String(d.department_id)) || {}).name || '—'
          }))

          this.stats = {
            loading: false,
            avg: avgScore !== null ? avgScore.toFixed(1) : null,
            max_score: max,
            total,
            satisfied,
            unsatisfied,
            distribution: dist,
            trend,
            by_department: byDept,
            by_agent: data.by_agent || []
          }
        }
      } catch (e) {
        console.error('[CSAT] loadStats error', e)
        this.stats.loading = false
      }
    },

    async loadEntries (page = 1) {
      this.entries.loading = true
      try {
        const extra = { page, per_page: 25 }
        if (this.showUnsatisfiedOnly) extra.max_score = this.unsatisfiedThreshold
        if (this.searchQuery) extra.search = this.searchQuery

        const res = await fetch(`${API}/customers/csat/entries?${this.buildParams(extra)}`, { headers: this.headers() })
        const data = await res.json()
        if (data.success) {
          this.entries = {
            loading: false,
            rows: data.entries || [],
            total: data.total || 0,
            page: data.page || 1,
            totalPages: data.total_pages || 1
          }
        }
      } catch (e) {
        console.error('[CSAT] loadEntries error', e)
        this.entries.loading = false
      }
    },

    distPercent (score) {
      const total = Object.values(this.stats.distribution).reduce((a, b) => a + parseInt(b), 0)
      if (!total) return 0
      return Math.round(((this.stats.distribution[score] || 0) / total) * 100)
    },

    distBarClass (score) {
      const max = this.stats.max_score || 5
      const ratio = score / max
      if (ratio >= 0.8) return 'cs-dist-bar--green'
      if (ratio >= 0.6) return 'cs-dist-bar--yellow'
      if (ratio >= 0.4) return 'cs-dist-bar--orange'
      return 'cs-dist-bar--red'
    },

    scoreBadgeClass (score, maxScore) {
      const ratio = score / (maxScore || 5)
      if (ratio >= 0.8) return 'cs-badge--great'
      if (ratio >= 0.6) return 'cs-badge--ok'
      if (ratio >= 0.4) return 'cs-badge--warn'
      return 'cs-badge--poor'
    },

    formatDate (dt) {
      if (!dt) return '—'
      try {
        return new Date(dt).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch { return dt }
    },

    openProfile (waId) {
      this.$router.push({ path: '/customers', query: { wa_id: waId } })
    },

    openChat (waId) {
      this.$router.push({ path: '/whatsapp', query: { phone: waId } })
    },

    exportCSV () {
      if (!this.entries.rows.length) return
      const headers = ['Customer', 'Phone', 'Score', 'Max Score', 'Department', 'Agent', 'Date', 'Feedback']
      const rows = this.entries.rows.map(r => [
        `"${(r.customer_name || '').replace(/"/g, '""')}"`,
        r.wa_id,
        r.score,
        r.max_score,
        `"${(r.department_name || '').replace(/"/g, '""')}"`,
        `"${(r.agent_name || '').replace(/"/g, '""')}"`,
        r.submitted_at,
        `"${(r.feedback_text || '').replace(/"/g, '""')}"`
      ])
      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `csat-export-${this.period}.csv`; a.click()
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
/* ── Layout shell (mirrors other views using dashboard-layout) ── */
.dashboard-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}
.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.page-content {
  flex: 1;
}

/* ── Page shell ── */
.cs-page { padding: 24px; width: 100%; box-sizing: border-box; }

/* ── Header ── */
.cs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 12px;
}
.cs-header-left { display: flex; align-items: center; gap: 14px; }
.cs-header-icon {
  font-size: 32px;
  width: 52px; height: 52px;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border-radius: 14px;
  flex-shrink: 0;
}
.cs-title { font-size: 22px; font-weight: 700; color: var(--text-primary); margin: 0 0 2px; }
.cs-subtitle { font-size: 13px; color: var(--text-secondary); margin: 0; }
.cs-export-btn {
  display: flex; align-items: center; gap: 6px;
  padding: 7px 14px;
  border: 1.5px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 12px; font-weight: 500;
  cursor: pointer;
  transition: border-color .15s, color .15s;
}
.cs-export-btn:hover { border-color: #f59e0b; color: #92400e; }

/* ── No-flow banner ── */
.cs-no-flow-banner {
  display: flex; gap: 12px; align-items: flex-start;
  background: #fefce8; border: 1.5px solid #fde047;
  border-radius: 10px; padding: 14px 16px;
  margin-bottom: 20px;
}
.cs-no-flow-icon { font-size: 22px; flex-shrink: 0; }
.cs-no-flow-banner strong { display: block; color: #713f12; font-size: 13px; margin-bottom: 3px; }
.cs-no-flow-banner p { margin: 0; font-size: 12px; color: #92400e; line-height: 1.5; }

/* ── Filters ── */
.cs-filters {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 20px; flex-wrap: wrap;
}
.cs-period-pills { display: flex; gap: 4px; background: rgba(0,0,0,.06); border-radius: 8px; padding: 3px; }
.cs-period-pill {
  padding: 4px 10px; border: none; border-radius: 6px;
  background: transparent; color: var(--text-secondary);
  font-size: 12px; font-weight: 500; cursor: pointer;
  transition: background .15s, color .15s;
}
.cs-period-pill.active { background: var(--card-background); color: var(--text-primary); box-shadow: 0 1px 3px rgba(0,0,0,.12); }
.cs-filter-select {
  padding: 6px 10px; border: 1.5px solid var(--border-primary);
  border-radius: 8px; background: var(--bg-primary);
  color: var(--text-primary); font-size: 12px; cursor: pointer;
  min-width: 140px;
}
.cs-unsatisfied-btn {
  display: flex; align-items: center; gap: 5px;
  padding: 6px 12px; border: 1.5px solid var(--border-primary);
  border-radius: 8px; background: var(--bg-primary);
  color: var(--text-secondary); font-size: 12px; font-weight: 500;
  cursor: pointer; transition: border-color .15s, background .15s, color .15s;
}
.cs-unsatisfied-btn.active {
  border-color: #ef4444; background: #fef2f2; color: #b91c1c;
}

/* ── Summary cards ── */
.cs-summary-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}
.cs-stat-card {
  background: var(--card-background);
  border: 1px solid var(--border-primary);
  border-radius: 14px; padding: 18px 20px;
}
.cs-stat-card--accent { border-color: #fde68a; background: linear-gradient(135deg, #fffbeb, var(--card-background)); }
.cs-stat-card--green  { border-color: #bbf7d0; }
.cs-stat-card--red    { border-color: #fecaca; }
.cs-stat-loading {
  background: var(--border-primary);
  border-radius: 14px; height: 110px;
  animation: cs-pulse 1.4s ease-in-out infinite;
}
@keyframes cs-pulse { 0%,100% { opacity: .6; } 50% { opacity: 1; } }
.cs-stat-label { font-size: 11px; font-weight: 600; color: var(--text-secondary); text-transform: uppercase; letter-spacing: .04em; margin-bottom: 8px; }
.cs-stat-main { display: flex; align-items: baseline; gap: 4px; margin-bottom: 4px; }
.cs-stat-big { font-size: 36px; font-weight: 800; color: var(--text-primary); line-height: 1; }
.cs-stat-denom { font-size: 16px; color: var(--text-secondary); }
.cs-stat-sub { font-size: 11px; color: var(--text-secondary); }
.cs-stars-row { display: flex; gap: 2px; margin-top: 4px; }
.cs-star { font-size: 18px; transition: color .1s; }
.cs-star--filled { color: #f59e0b; }
.cs-star--empty  { color: #d1d5db; }

/* ── Analytics row ── */
.cs-analytics-row {
  display: flex; gap: 14px;
  margin-bottom: 20px; flex-wrap: wrap;
}
.cs-analytics-card {
  flex: 1; min-width: 260px;
  background: var(--card-background);
  border: 1px solid var(--border-primary);
  border-radius: 14px; padding: 18px 20px;
}
.cs-analytics-head {
  font-size: 12px; font-weight: 700; color: var(--text-primary);
  text-transform: uppercase; letter-spacing: .04em;
  margin-bottom: 14px; padding-bottom: 10px;
  border-bottom: 1px solid var(--border-primary);
}
.cs-loading-rows { display: flex; flex-direction: column; gap: 8px; }
.cs-loading-bar { height: 14px; border-radius: 4px; background: var(--border-primary); animation: cs-pulse 1.4s ease-in-out infinite; }
.cs-empty-state { text-align: center; color: var(--text-secondary); font-size: 13px; padding: 24px 0; }

/* Distribution */
.cs-dist-rows { display: flex; flex-direction: column; gap: 8px; }
.cs-dist-row { display: flex; align-items: center; gap: 8px; }
.cs-dist-label { font-size: 12px; font-weight: 600; color: var(--text-secondary); width: 32px; flex-shrink: 0; }
.cs-dist-bar-wrap { flex: 1; height: 8px; background: var(--border-primary); border-radius: 99px; overflow: hidden; }
.cs-dist-bar { height: 100%; border-radius: 99px; transition: width .4s ease; min-width: 4px; }
.cs-dist-bar--green  { background: #22c55e; }
.cs-dist-bar--yellow { background: #eab308; }
.cs-dist-bar--orange { background: #f97316; }
.cs-dist-bar--red    { background: #ef4444; }
.cs-dist-count { font-size: 12px; color: var(--text-primary); font-weight: 600; width: 28px; text-align: right; flex-shrink: 0; }
.cs-dist-pct { font-size: 11px; color: var(--text-secondary); width: 34px; text-align: right; flex-shrink: 0; }

/* Trend sparkline */
.cs-sparkline { width: 100%; height: 80px; display: block; margin-bottom: 4px; }
.cs-trend-labels { display: flex; justify-content: space-between; margin-bottom: 6px; }
.cs-trend-label { font-size: 10px; color: var(--text-secondary); }
.cs-trend-avgs { display: flex; justify-content: space-between; }
.cs-trend-avg-chip { display: flex; flex-direction: column; align-items: center; gap: 1px; }
.cs-trend-avg-score { font-size: 12px; font-weight: 700; color: #b45309; }
.cs-trend-avg-cnt   { font-size: 10px; color: var(--text-secondary); }

/* By department */
.cs-by-rows { display: flex; flex-direction: column; gap: 6px; }
.cs-by-row { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-bottom: 1px solid var(--border-primary); }
.cs-by-row:last-child { border-bottom: none; }
.cs-by-name { flex: 1; font-size: 12px; color: var(--text-primary); font-weight: 500; }
.cs-by-score { font-size: 12px; font-weight: 700; color: #b45309; min-width: 50px; text-align: right; }
.cs-by-count { font-size: 11px; color: var(--text-secondary); background: var(--border-primary); border-radius: 99px; padding: 1px 7px; }

/* ── Table ── */
.cs-table-card {
  background: var(--card-background);
  border: 1px solid var(--border-primary);
  border-radius: 14px; overflow: hidden;
}
.cs-table-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid var(--border-primary);
  gap: 12px; flex-wrap: wrap;
}
.cs-table-title { font-size: 13px; font-weight: 700; color: var(--text-primary); }
.cs-table-count { font-weight: 400; color: var(--text-secondary); margin-left: 4px; }
.cs-table-search {
  display: flex; align-items: center; gap: 7px;
  background: var(--bg-primary); border: 1.5px solid var(--border-primary);
  border-radius: 8px; padding: 5px 10px;
}
.cs-search-input { border: none; background: transparent; color: var(--text-primary); font-size: 12px; outline: none; width: 220px; }
.cs-table-wrap { overflow-x: auto; }
.cs-table-loading { display: flex; justify-content: center; align-items: center; padding: 40px; }
.cs-table {
  width: 100%; border-collapse: collapse; font-size: 13px;
}
.cs-table thead th {
  padding: 10px 16px; text-align: left;
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em;
  color: var(--text-secondary); background: var(--bg-primary);
  border-bottom: 1px solid var(--border-primary);
}
.cs-table tbody tr {
  border-bottom: 1px solid var(--border-primary);
  transition: background .12s;
}
.cs-table tbody tr:last-child { border-bottom: none; }
.cs-table tbody tr:hover { background: var(--bg-primary); }
.cs-row-unsatisfied { background: #fff5f5 !important; }
.cs-table td { padding: 10px 16px; vertical-align: middle; }
.cs-table-empty { text-align: center; color: var(--text-secondary); padding: 32px !important; }
.cs-td-customer .cs-customer-name { font-weight: 600; color: var(--text-primary); }
.cs-td-customer .cs-customer-phone { font-size: 11px; color: var(--text-secondary); margin-top: 1px; }
.cs-score-badge {
  display: inline-block; padding: 2px 8px;
  border-radius: 99px; font-size: 11px; font-weight: 700;
  margin-bottom: 3px;
}
.cs-badge--great { background: #dcfce7; color: #166534; }
.cs-badge--ok    { background: #fef9c3; color: #854d0e; }
.cs-badge--warn  { background: #ffedd5; color: #9a3412; }
.cs-badge--poor  { background: #fee2e2; color: #991b1b; }
.cs-score-stars { display: flex; gap: 1px; }
.cs-star-sm { font-size: 11px; color: #d1d5db; }
.cs-star-sm.filled { color: #f59e0b; }
.cs-td-dept, .cs-td-agent { color: var(--text-secondary); font-size: 12px; }
.cs-td-date { font-size: 12px; color: var(--text-secondary); white-space: nowrap; }
.cs-td-actions { display: flex; gap: 6px; }
.cs-action-btn {
  display: flex; align-items: center; gap: 4px;
  padding: 4px 9px; border-radius: 6px;
  font-size: 11px; font-weight: 500;
  cursor: pointer; border: 1.5px solid transparent;
  transition: background .12s, border-color .12s, color .12s;
}
.cs-action-btn--profile {
  background: rgba(14,165,233,.08); color: #0369a1;
  border-color: rgba(14,165,233,.25);
}
.cs-action-btn--profile:hover { background: rgba(14,165,233,.16); }
.cs-action-btn--chat {
  background: rgba(34,197,94,.08); color: #15803d;
  border-color: rgba(34,197,94,.25);
}
.cs-action-btn--chat:hover { background: rgba(34,197,94,.16); }

/* ── Pagination ── */
.cs-pagination {
  display: flex; align-items: center; justify-content: center; gap: 14px;
  padding: 14px;
  border-top: 1px solid var(--border-primary);
}
.cs-page-btn {
  padding: 5px 14px; border: 1.5px solid var(--border-primary);
  border-radius: 7px; background: var(--bg-primary);
  color: var(--text-secondary); font-size: 12px; font-weight: 500;
  cursor: pointer; transition: border-color .12s, color .12s;
}
.cs-page-btn:disabled { opacity: .4; cursor: not-allowed; }
.cs-page-btn:not(:disabled):hover { border-color: #f59e0b; color: #92400e; }
.cs-page-info { font-size: 12px; color: var(--text-secondary); }

/* ── Responsive ── */
@media (max-width: 900px) {
  .cs-summary-row { grid-template-columns: repeat(2, 1fr); }
  .cs-analytics-row { flex-direction: column; }
}
@media (max-width: 600px) {
  .cs-summary-row { grid-template-columns: 1fr 1fr; }
  .cs-page { padding: 16px; }
}
</style>
